import type { Region } from "@packages/client";

// ============================================================================
// Onboarding Type Constants
// ============================================================================

export const onboardingTypes = {
  default: "",
  demo: "demo",
  migrate: "migrate",
  scratch: "scratch",
  kickstart: "kickstart"
} as const;

export type OnboardingType =
  (typeof onboardingTypes)[keyof typeof onboardingTypes];

/**
 * Whether a query-string value names a flow.
 *
 * Values, not keys: `onboardingTypes.default` is the empty string, so the
 * key-based shape `isGitProvider` uses would accept the literal "default" and
 * reject nothing else. The default is excluded on top of that — storing it says
 * the same thing as storing nothing.
 */
export const isOnboardingType = (
  value: string | null | undefined
): value is OnboardingType =>
  value !== onboardingTypes.default &&
  Object.values(onboardingTypes).some(type => type === value);

// ============================================================================
// Error Type Constants
// ============================================================================

export const errorTypes = {
  default: "",
  retry: "retry",
  deployment: "deployment",
  redirect: "redirect",
  custom: "custom",
  provisioningFailure: "provisioningFailure"
} as const;

export type ErrorType = (typeof errorTypes)[keyof typeof errorTypes];

// ============================================================================
// Verification Type Constants
// ============================================================================

export const verificationTypes = {
  default: "unset",
  none: "none",
  full: "full",
  ticket: "ticket",
  creditCard: "credit-card",
  idle: "idle"
} as const;

export type VerificationType =
  (typeof verificationTypes)[keyof typeof verificationTypes];

// ============================================================================
// Onboarding Phase Constants (Simplified Hybrid Flow)
// ============================================================================

/**
 * Onboarding phases represent the high-level wizard steps.
 * Unlike step numbers, phases are named states that make the flow explicit.
 *
 * URL mapping:
 * - type-selection       → /create-project
 * - repository-selection → /create-project/repository
 * - project-details      → /create-project/details
 * - verification         → /create-project/verify
 *
 * After project creation, users are redirected to the organization page
 * where the in-project setup wizard guides them through configuration.
 */
export const onboardingPhases = {
  typeSelection: "type-selection",
  repositorySelection: "repository-selection",
  projectDetails: "project-details",
  verification: "verification"
} as const;

export type OnboardingPhase =
  (typeof onboardingPhases)[keyof typeof onboardingPhases];

/**
 * Maps phases to URL paths (relative to /:orgId/create-project)
 */
export const phaseToPath: Record<OnboardingPhase, string> = {
  [onboardingPhases.typeSelection]: "",
  [onboardingPhases.repositorySelection]: "repository",
  [onboardingPhases.projectDetails]: "details",
  [onboardingPhases.verification]: "verify"
};

/**
 * Query flag on /create-project that keeps the provider list on screen.
 *
 * An organization with the GitHub App installed is sent straight to repository
 * selection, so the list is only reachable by navigating *back* to it. The flag
 * is what marks that navigation as deliberate; it lives in the URL so it
 * survives a reload.
 */
export const showProvidersParam = "providers";

/**
 * Query param naming the page that sent the user to the one they are on, as an
 * `onboardingPhases` value.
 *
 * Only Back needs it, and only where a page has two ways in. The details form
 * is reached from the list or from the repository page's other-providers footer;
 * repository selection is reached from the list or by the auto-jump, which makes
 * it the first screen of the flow. Back runs a route later, when nothing else
 * distinguishes those, so the origin travels in the URL — where it also survives
 * a reload.
 */
export const fromParam = "from";

// ============================================================================
// Git Provider Types
// ============================================================================

/**
 * The route the user picked on the create page. "local" is not a git host —
 * it selects the CLI/push panel — but it travels the same way as the others,
 * so the wizard resolves all four from one value.
 */
export const gitProviders = {
  github: "github",
  gitlab: "gitlab",
  bitbucket: "bitbucket",
  local: "local"
} as const;

export type GitProvider =
  (typeof gitProviders)[keyof typeof gitProviders] | null;

/**
 * Query param carrying the chosen provider from the create flow to the
 * in-project wizard, appended to the project URL on creation success. The URL
 * is the only carrier: it makes the wizard step addressable, so browser back
 * and a shared link both land on the right panel.
 */
export const connectParam = "connect";

/**
 * Anything can be in a query string, including `constructor` and the rest of
 * `Object.prototype` — hence `Object.hasOwn` rather than `in`.
 */
export const isGitProvider = (
  value: string | null | undefined
): value is NonNullable<GitProvider> =>
  typeof value === "string" && Object.hasOwn(gitProviders, value);

export type GithubOrganization = {
  gh_installation_id: string;
  gh_organization_name: string;
  organization_id: string;
  id: string;
  created_at: string;
  updated_at: string;
};

export type FormattedGithubOrganization = {
  value: string;
  label: string;
};

export type FormattedGithubRepository = {
  value: string;
  label: string;
  defaultBranch: string;
};

export interface GithubRepositories {
  total_count: number;
  repositories: GithubRepository[];
}

export interface GithubBranch {
  commit: { sha: string; url: string };
  name: string;
  protected: boolean;
}

export interface GithubRepository {
  id: number;
  node_id: string;
  name: string;
  full_name: string;
  default_branch: string;
  html_url: string;
  clone_url: string;
  private: boolean;
}

export type GithubAppErrorCode =
  | "invalid_state"
  | "exchange_failed"
  | "duplicate_installation"
  | "invalid_installation";

// ============================================================================
// Wizard State Types
// ============================================================================

export type WizardState = {
  /** Selected onboarding type (demo, migrate, kickstart, scratch) */
  onboardingType: OnboardingType;
};

// ============================================================================
// Project Details State
// ============================================================================

export type ProjectDetailsState = {
  /** Project name entered by user */
  projectName: string;

  /** Production environment/branch name */
  productionEnvironment: string;

  /** Selected region for project deployment */
  region: Region | null;
};

// ============================================================================
// Git Provider State
// ============================================================================

export type GitProviderState = {
  /** Currently selected git provider */
  provider: GitProvider;

  /** Loading state for git provider operations */
  isLoading: boolean;

  /** Error message from git provider operations */
  error: string | null;

  /** GitHub-specific state */
  github: {
    /** Whether organizations have been fetched (prevents infinite re-fetch) */
    hasFetchedOrganizations: boolean;

    /** Available GitHub organizations */
    organizations: GithubOrganization[];

    /** Selected GitHub organization installation ID */
    selectedOrganizationId: string | null;

    /** Selected GitHub organization label */
    selectedOrganizationLabel: string | null;

    /** Available repositories for selected organization */
    repositories: GithubRepository[];

    /** Selected repository name */
    selectedRepository: string | null;

    /** Selected repository default branch */
    selectedRepositoryDefaultBranch: string | null;

    /** Available branches for selected repository */
    branches: GithubBranch[];

    /** Selected branch name */
    selectedBranch: string | null;

    /** GitHub App installation ID */
    installationId: string | null;
  };
};

// ============================================================================
// Verification State
// ============================================================================

export type VerificationState = {
  /** Current verification type/status */
  type: VerificationType;

  /** Stripe credentials for payment */
  credentials: {
    client_secret?: string;
    public_key?: string;
    automatic_payment_methods?: boolean;
  } | null;
};

// ============================================================================
// Project Creation State
// ============================================================================

export type ProjectCreationProgressState = {
  /** Whether project creation is in progress */
  isLoading: boolean;

  /**
   * Error type if creation failed. The detail behind it travels in the thunk's
   * rejection rather than the store — `Details` renders a retry control off the
   * type alone.
   */
  errorType: ErrorType;

  /**
   * Whether this flow already created its project.
   *
   * The pages it walked through are still in the browser's history, and every
   * one of them is spent: the form would create a second project and the list
   * is a page the user did not ask for. `Routes` reads this to send a walk back
   * into them to the project list instead. Cleared by `resetOnboardingState`.
   */
  hasCreatedProject: boolean;
};

// ============================================================================
// Organization Project Creation State (per organization)
// ============================================================================

export type OrganizationProjectCreationState = {
  /** Wizard navigation state */
  wizard: WizardState;

  /** Project details entered by user */
  projectDetails: ProjectDetailsState;

  /** Git provider integration state */
  gitProvider: GitProviderState;

  /** Verification/billing state */
  verification: VerificationState;

  /** Project creation progress state */
  projectCreation: ProjectCreationProgressState;
};

// ============================================================================
// Root Project Creation Slice State
// ============================================================================

/**
 * Root project creation Redux slice state.
 *
 * Organizes project creation state by organization ID for efficient lookup and updates.
 * Each organization's project creation flow is stored independently to avoid
 * interference when switching between organizations.
 *
 * Structure: Normalized state pattern (byId)
 * - Key: organizationId (UUID)
 * - Value: OrganizationProjectCreationState
 */
export type ProjectCreationSliceState = {
  byOrganizationId: Record<string, OrganizationProjectCreationState>;
};

// ============================================================================
// Initial State Helpers
// ============================================================================

export const initialGitProviderState: GitProviderState = {
  provider: null,
  isLoading: false,
  error: null,
  github: {
    hasFetchedOrganizations: false,
    organizations: [],
    selectedOrganizationId: null,
    selectedOrganizationLabel: null,
    repositories: [],
    selectedRepository: null,
    selectedRepositoryDefaultBranch: null,
    branches: [],
    selectedBranch: null,
    installationId: null
  }
};

export const initialVerificationState: VerificationState = {
  type: verificationTypes.default,
  credentials: null
};

export const initialProjectCreationState: ProjectCreationProgressState = {
  isLoading: false,
  errorType: errorTypes.default,
  hasCreatedProject: false
};

export const initialWizardState: WizardState = {
  onboardingType: onboardingTypes.default
};

export const initialProjectDetailsState: ProjectDetailsState = {
  projectName: "",
  productionEnvironment: "",
  region: null
};

export const createInitialOrganizationState =
  (): OrganizationProjectCreationState => ({
    wizard: { ...initialWizardState },
    projectDetails: { ...initialProjectDetailsState },
    gitProvider: {
      ...initialGitProviderState,
      github: {
        ...initialGitProviderState.github,
        organizations: [],
        repositories: [],
        branches: []
      }
    },
    verification: { ...initialVerificationState },
    projectCreation: { ...initialProjectCreationState }
  });
